import type { DatePreset, DateRange } from '../types'
import { getDateRangeFromPreset, resolveDateRange } from './index'

// Date-picker <-> domain DateRange helpers, shared by the Sales and Sub-Customers dashboards.

export const PICKER_PRESET_ORDER: DatePreset[] = [
  'today', 'this_week', 'this_month', 'this_quarter', 'this_year', 'last_30_days',
]

export function parseIsoDate(date: string): Date {
  const [year, month, day] = date.split('-').map(Number)
  return new Date(year, month - 1, day)
}

export function toIsoDate(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export function getPresetRangeAsDates(preset: DatePreset): { from: Date; to: Date } {
  const resolved = getDateRangeFromPreset(preset)
  return { from: parseIsoDate(resolved.from), to: parseIsoDate(resolved.to) }
}

function findMatchingPreset(range: { from: Date; to: Date }): DatePreset | null {
  const selectedFrom = toIsoDate(range.from)
  const selectedTo = toIsoDate(range.to)
  for (const preset of PICKER_PRESET_ORDER) {
    const presetRange = getPresetRangeAsDates(preset)
    if (toIsoDate(presetRange.from) === selectedFrom && toIsoDate(presetRange.to) === selectedTo) {
      return preset
    }
  }
  return null
}

export function resolvePickerRange(value: DateRange): { from: Date | undefined; to: Date | undefined } | undefined {
  if (value.preset === 'custom' && (!value.from || !value.to)) return undefined
  const resolved = resolveDateRange(value)
  return { from: parseIsoDate(resolved.from), to: parseIsoDate(resolved.to) }
}

export function toDomainDateRange(range: { from: Date | undefined; to: Date | undefined }): DateRange {
  if (!range.from || !range.to) return { preset: 'custom', from: null, to: null }
  const matched = findMatchingPreset({ from: range.from, to: range.to })
  if (matched) return { preset: matched, from: null, to: null }
  return { preset: 'custom', from: toIsoDate(range.from), to: toIsoDate(range.to) }
}

/** A custom range is usable only once both ends are picked; presets always are. */
export function isDateRangeComplete(range: DateRange): boolean {
  return range.preset !== 'custom' || Boolean(range.from && range.to)
}

/** Inclusive day count between two YYYY-MM-DD dates. */
export function countRangeDays(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000) + 1
}

/** Every day from..to (inclusive) gets a row, so bar charts show gaps as zero bars. */
export function fillDateGaps<T extends { date: string }>(
  rows: T[],
  from: string,
  to: string,
  emptyRow: (date: string) => T,
): T[] {
  const byDate = new Map(rows.map((row) => [row.date, row]))
  const filled: T[] = []
  const end = parseIsoDate(to)
  for (const cursor = parseIsoDate(from); cursor <= end; cursor.setDate(cursor.getDate() + 1)) {
    const date = toIsoDate(cursor)
    filled.push(byDate.get(date) ?? emptyRow(date))
  }
  return filled
}
